import { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Search, Plus, Pencil, Trash2, Pin, PinOff,
  AlertTriangle, RefreshCw, Send, Users, ShieldAlert,
  Calendar, CheckCircle2, Clock, Eye, Sparkles, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../api/client';
import { Modal } from '../../../components/ui/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { PaginationFooter } from '../../../components/ui/DataTable';

interface MasterBroadcastItem {
  id: string;
  titulo: string;
  corpo: string;
  tipo: string;
  categoria: string;
  destinatario: string;
  status: string;
  fixado: boolean;
  ativo: boolean;
  criadoEm: string;
  criadoPor?: { id: string; nome: string; email: string; perfil: string };
  configPreview?: {
    isMasterBroadcast?: boolean;
    destinatarios?: string[];
    notificadoEm?: string | null;
  };
  _count?: { visualizacoes: number };
}

const TIPO_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  AVISO:    { bg: 'rgba(245, 158, 11, 0.12)', color: '#d97706', border: 'rgba(245, 158, 11, 0.25)' },
  URGENTE:  { bg: 'rgba(239, 68, 68, 0.12)',  color: '#dc2626', border: 'rgba(239, 68, 68, 0.25)' },
  EVENTO:   { bg: 'rgba(139, 92, 246, 0.12)', color: '#7c3aed', border: 'rgba(139, 92, 246, 0.25)' },
  REUNIAO:  { bg: 'rgba(16, 185, 129, 0.12)', color: '#059669', border: 'rgba(16, 185, 129, 0.25)' },
  LEMBRETE: { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb', border: 'rgba(59, 130, 246, 0.25)' },
};

const DESTINATARIOS_OPCOES = [
  { id: 'TODOS', label: 'Toda a Plataforma' },
  { id: 'PREFEITURAS', label: 'Prefeituras / Secretarias' },
  { id: 'ESTADOS', label: 'Secretarias Estaduais' },
  { id: 'ESCOLAS', label: 'Direções Escolares' },
  { id: 'REDES', label: 'Gestores de Rede' },
];

export function MasterBroadcastTab() {
  const [items, setItems] = useState<MasterBroadcastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [total, setTotal] = useState(0);

  // Modal de Criação / Edição
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MasterBroadcastItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MasterBroadcastItem | null>(null);

  const [form, setForm] = useState({
    titulo: '',
    corpo: '',
    tipo: 'AVISO',
    categoria: 'INSTITUCIONAL',
    destinatarios: ['TODOS'] as string[],
    fixado: false,
    notificar: true,
  });

  const fetchBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (filterTipo) params.set('tipo', filterTipo);
      params.set('page', String(page));
      params.set('limit', String(pageSize));

      const { data } = await api.get(`/master/comunicados/broadcast?${params.toString()}`);
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Erro ao carregar avisos da plataforma');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, filterTipo, page, pageSize]);

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const openNew = () => {
    setEditing(null);
    setForm({
      titulo: '',
      corpo: '',
      tipo: 'AVISO',
      categoria: 'INSTITUCIONAL',
      destinatarios: ['TODOS'],
      fixado: false,
      notificar: true,
    });
    setModalOpen(true);
  };

  const openEdit = (item: MasterBroadcastItem) => {
    setEditing(item);
    setForm({
      titulo: item.titulo,
      corpo: item.corpo,
      tipo: item.tipo,
      categoria: item.categoria || 'INSTITUCIONAL',
      destinatarios: item.configPreview?.destinatarios || ['TODOS'],
      fixado: Boolean(item.fixado),
      notificar: false,
    });
    setModalOpen(true);
  };

  const toggleDestinatario = (destId: string) => {
    if (destId === 'TODOS') {
      setForm((prev) => ({ ...prev, destinatarios: ['TODOS'] }));
      return;
    }
    setForm((prev) => {
      let next = prev.destinatarios.filter((d) => d !== 'TODOS');
      if (next.includes(destId)) {
        next = next.filter((d) => d !== destId);
        if (next.length === 0) next = ['TODOS'];
      } else {
        next.push(destId);
      }
      return { ...prev, destinatarios: next };
    });
  };

  const handleSave = async () => {
    if (!form.titulo.trim() || !form.corpo.trim()) {
      toast.error('Título e conteúdo são obrigatórios.');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/master/comunicados/broadcast/${editing.id}`, {
          titulo: form.titulo,
          corpo: form.corpo,
          tipo: form.tipo,
          categoria: form.categoria,
          destinatarios: form.destinatarios,
          fixado: form.fixado,
        });
        toast.success('Aviso global atualizado com sucesso!');
      } else {
        await api.post('/master/comunicados/broadcast', {
          titulo: form.titulo,
          corpo: form.corpo,
          tipo: form.tipo,
          categoria: form.categoria,
          destinatarios: form.destinatarios,
          fixado: form.fixado,
          notificar: form.notificar,
        });
        toast.success('Aviso global publicado para a plataforma!');
      }
      setModalOpen(false);
      fetchBroadcasts();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar comunicado');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFixar = async (item: MasterBroadcastItem) => {
    try {
      await api.patch(`/master/comunicados/broadcast/${item.id}`, {
        fixado: !item.fixado,
      });
      toast.success(item.fixado ? 'Aviso desfixado do topo' : 'Aviso fixado no topo!');
      fetchBroadcasts();
    } catch {
      toast.error('Erro ao alternar fixação');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/master/comunicados/broadcast/${deleteTarget.id}`);
      toast.success('Aviso removido com sucesso!');
      setDeleteTarget(null);
      fetchBroadcasts();
    } catch {
      toast.error('Erro ao remover aviso');
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Toolbar com Bordas Nítidas ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, padding: '12px 16px', background: '#ffffff',
        borderRadius: 12, border: '1.5px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 260 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar avisos globais da plataforma..."
              style={{
                width: '100%', padding: '8px 12px 8px 34px', borderRadius: 8,
                border: '1.5px solid #cbd5e1', background: '#ffffff',
                fontSize: '.85rem', color: '#0f172a', outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3af' }}>
                <X size={12} />
              </button>
            )}
          </div>

          <select
            value={filterTipo}
            onChange={(e) => { setFilterTipo(e.target.value); setPage(1); }}
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1',
              background: '#ffffff', fontSize: '.85rem', color: '#0f172a', fontWeight: 500,
              outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <option value="">Todos os Tipos</option>
            <option value="AVISO">Aviso</option>
            <option value="URGENTE">Urgente</option>
            <option value="EVENTO">Evento</option>
            <option value="REUNIAO">Reunião</option>
            <option value="LEMBRETE">Lembrete</option>
          </select>
        </div>

        <button
          onClick={openNew}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
            borderRadius: 8, background: 'linear-gradient(135deg, #1B365D 0%, #2563eb 100%)',
            color: '#ffffff', fontWeight: 600, fontSize: '.85rem', border: 'none',
            cursor: 'pointer', boxShadow: '0 2px 6px rgba(37,99,235,.25)',
          }}
        >
          <Plus size={15} /> Novo Aviso Global
        </button>
      </div>

      {/* ── Lista de Avisos (Cards) ── */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <RefreshCw size={18} className="animate-spin" /> Carregando avisos da plataforma...
        </div>
      ) : items.length === 0 ? (
        <div style={{
          padding: 60, textAlign: 'center', background: '#ffffff',
          borderRadius: 12, border: '1.5px dashed #cbd5e1', color: '#64748b',
        }}>
          <Megaphone size={36} style={{ margin: '0 auto 12px', opacity: 0.35, color: '#3b82f6' }} />
          <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
            Nenhum aviso global cadastrado
          </h3>
          <p style={{ margin: 0, fontSize: '.85rem' }}>
            Publique comunicados institucionais, avisos de manutenção ou comunicados do MEC para toda a rede.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {items.map((item) => {
            const tc = TIPO_COLORS[item.tipo] || TIPO_COLORS.AVISO;
            const dests = item.configPreview?.destinatarios || ['TODOS'];
            return (
              <div
                key={item.id}
                style={{
                  background: '#ffffff', borderRadius: 12,
                  border: item.fixado ? '1.5px solid #3b82f6' : '1.5px solid #e2e8f0',
                  boxShadow: item.fixado ? '0 4px 14px rgba(59,130,246,.12)' : '0 1px 3px rgba(0,0,0,.04)',
                  padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
                  position: 'relative', overflow: 'hidden',
                }}
              >
                {item.fixado && (
                  <div style={{
                    position: 'absolute', top: 0, right: 0, background: '#3b82f6',
                    color: '#fff', fontSize: '.68rem', fontWeight: 700, padding: '2px 10px',
                    borderBottomLeftRadius: 8, display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <Pin size={10} /> FIXADO
                  </div>
                )}

                {/* Top Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
                    background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                    textTransform: 'uppercase',
                  }}>
                    {item.tipo}
                  </span>
                  <span style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: '.68rem', fontWeight: 600,
                    background: 'rgba(30, 58, 138, 0.08)', color: '#1e3a8a',
                  }}>
                    {item.categoria}
                  </span>
                  <span style={{ fontSize: '.72rem', color: '#64748b', marginLeft: 'auto' }}>
                    <Clock size={11} style={{ display: 'inline', marginRight: 3 }} />
                    {formatDate(item.criadoEm)}
                  </span>
                </div>

                {/* Título & Corpo */}
                <div>
                  <h4 style={{ margin: '0 0 6px', fontSize: '.96rem', fontWeight: 700, color: '#0f172a' }}>
                    {item.titulo}
                  </h4>
                  <p style={{
                    margin: 0, fontSize: '.84rem', color: '#334155',
                    lineHeight: 1.45, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {item.corpo}
                  </p>
                </div>

                {/* Destinatários Badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                  <Users size={12} style={{ color: '#94a3b8' }} />
                  {dests.map((d) => (
                    <span
                      key={d}
                      style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: '.68rem', fontWeight: 600,
                        background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0',
                      }}
                    >
                      {d}
                    </span>
                  ))}
                  {item._count?.visualizacoes !== undefined && (
                    <span style={{ marginLeft: 'auto', fontSize: '.72rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Eye size={12} /> {item._count.visualizacoes}
                    </span>
                  )}
                </div>

                {/* Bottom Actions */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  gap: 6, paddingTop: 10, borderTop: '1px solid #f1f5f9',
                }}>
                  <button
                    onClick={() => handleToggleFixar(item)}
                    style={{
                      padding: '5px 9px', borderRadius: 6, border: '1px solid #cbd5e1',
                      background: '#ffffff', cursor: 'pointer', fontSize: '.75rem', color: item.fixado ? '#3b82f6' : '#64748b',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                    title={item.fixado ? 'Desfixar aviso' : 'Fixar aviso no topo'}
                  >
                    {item.fixado ? <PinOff size={13} /> : <Pin size={13} />}
                  </button>
                  <button
                    onClick={() => openEdit(item)}
                    style={{
                      padding: '5px 9px', borderRadius: 6, border: '1px solid #cbd5e1',
                      background: '#ffffff', cursor: 'pointer', fontSize: '.75rem', color: '#334155',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                    title="Editar aviso"
                  >
                    <Pencil size={13} /> Editar
                  </button>
                  <button
                    onClick={() => setDeleteTarget(item)}
                    style={{
                      padding: '5px 9px', borderRadius: 6, border: '1px solid rgba(239, 68, 68, 0.3)',
                      background: 'rgba(239, 68, 68, 0.06)', cursor: 'pointer', fontSize: '.75rem', color: '#dc2626',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                    title="Remover aviso"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Paginação Única com Seletor ── */}
      {total > 0 && (
        <PaginationFooter
          pageIndex={page - 1}
          pageSize={pageSize}
          totalItems={total}
          onPageChange={(p) => setPage(p + 1)}
          pageSizeOptions={[12, 24, 48, 96]}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}

      {/* ── Modal de Criação / Edição ── */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar Aviso Global: ${editing.titulo}` : 'Novo Aviso Global da Plataforma'}
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '.84rem', marginBottom: 5 }}>
              Título do Comunicado *
            </label>
            <input
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Ex: Manutenção programada do sistema — Sábado às 22h"
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 8,
                border: '1.5px solid #cbd5e1', background: '#ffffff',
                fontSize: '.88rem', color: '#0f172a',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '.84rem', marginBottom: 5 }}>
                Tipo do Comunicado *
              </label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8,
                  border: '1.5px solid #cbd5e1', background: '#ffffff',
                  fontSize: '.85rem', color: '#0f172a',
                }}
              >
                <option value="AVISO">Aviso Informativo</option>
                <option value="URGENTE">Alerta Urgente</option>
                <option value="EVENTO">Evento / Notícia MEC</option>
                <option value="REUNIAO">Reunião / Diretriz</option>
                <option value="LEMBRETE">Lembrete Técnico</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '.84rem', marginBottom: 5 }}>
                Categoria *
              </label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8,
                  border: '1.5px solid #cbd5e1', background: '#ffffff',
                  fontSize: '.85rem', color: '#0f172a',
                }}
              >
                <option value="INSTITUCIONAL">Institucional da Plataforma</option>
                <option value="OPERACIONAL">Operacional / Técnico</option>
                <option value="TEMPLATE">Template / Modelo</option>
              </select>
            </div>
          </div>

          {/* Destinatários Segmentados */}
          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '.84rem', marginBottom: 6 }}>
              Público Destinatário
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {DESTINATARIOS_OPCOES.map((opt) => {
                const selected = form.destinatarios.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleDestinatario(opt.id)}
                    style={{
                      padding: '6px 12px', borderRadius: 20, fontSize: '.78rem', fontWeight: 600,
                      border: selected ? '1.5px solid #2563eb' : '1.5px solid #cbd5e1',
                      background: selected ? 'rgba(37,99,235,.1)' : '#ffffff',
                      color: selected ? '#1d4ed8' : '#334155', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {selected && <CheckCircle2 size={12} style={{ color: '#2563eb' }} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, fontSize: '.84rem', marginBottom: 5 }}>
              Conteúdo da Mensagem *
            </label>
            <textarea
              value={form.corpo}
              onChange={(e) => setForm({ ...form, corpo: e.target.value })}
              placeholder="Digite o comunicado completo..."
              rows={5}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1.5px solid #cbd5e1', background: '#ffffff',
                fontSize: '.88rem', color: '#0f172a', resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={form.fixado}
                onChange={(e) => setForm({ ...form, fixado: e.target.checked })}
              />
              <span>Fixar no topo dos murais</span>
            </label>

            {!editing && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={form.notificar}
                  onChange={(e) => setForm({ ...form, notificar: e.target.checked })}
                />
                <span>Enviar notificação em tempo real</span>
              </label>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvando...' : editing ? 'Salvar Alterações' : 'Publicar Aviso'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Modal de Confirmação de Exclusão ── */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Remover Aviso Global?"
        message={deleteTarget ? `Tem certeza que deseja remover o comunicado "${deleteTarget.titulo}"?` : ''}
        confirmLabel="Remover"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
